/**
 * What counts as a practice session somebody could actually have done.
 *
 * Logging is typed by a child on a phone, so the rules are about typos rather
 * than about cheating. A practice log is a record of effort, not a payroll
 * return: the point is that the numbers stay believable enough to be worth
 * looking at, not that they are impossible to inflate.
 */

/** Beyond this, it is a stuck key rather than a long afternoon. */
export const MAX_SESSION_MINUTES = 480;

/**
 * How far back a session may be logged.
 *
 * People write up a week at a time, so "now only" would be useless. A month is
 * long enough for that and short enough that nobody is quietly filling in a
 * term they did not practise.
 */
export const BACKDATE_DAYS = 30;

/** A minute of slack, so a phone whose clock runs fast is not refused. */
const FUTURE_TOLERANCE_MS = 60_000;

/** Null when the session is plausible; otherwise something to show the person. */
export function describeSessionProblem(
  minutes: number,
  startedAt: Date,
  now: Date = new Date(),
): string | null {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return "How long did you practise?";
  }
  if (minutes > MAX_SESSION_MINUTES) {
    return `That is more than ${MAX_SESSION_MINUTES / 60} hours — check the number.`;
  }

  const at = startedAt.getTime();
  if (!Number.isFinite(at)) return "That is not a date.";
  if (at > now.getTime() + FUTURE_TOLERANCE_MS) {
    return "That is in the future.";
  }
  if (at < now.getTime() - BACKDATE_DAYS * 86_400_000) {
    return `You can only log the last ${BACKDATE_DAYS} days.`;
  }
  return null;
}

/** Minutes as typed, seconds as stored. Rounded, because nobody types 7.5. */
export function minutesToSeconds(minutes: number): number {
  return Math.round(minutes * 60);
}

/**
 * A measured length, kept inside what a session can be.
 *
 * The eight-hour guard above is about a stuck key, and a clock cannot mistype
 * — so a running timer is clamped rather than refused. Refusing it would mean
 * a timer nobody could stop, which is worse than a total that is wrong at the
 * top end and marked as such.
 */
export function clampSessionSeconds(seconds: number): number {
  return Math.min(Math.max(0, Math.round(seconds)), MAX_SESSION_MINUTES * 60);
}

/* ── What they worked on ───────────────────────────────────────────────────
 *
 * Required, not optional. A log of bare numbers tells a tutor that somebody
 * practised and nothing about what, which is the half that makes the next
 * lesson different. It is one short line and the cost of asking for it is a
 * few seconds; the cost of not asking is a column of minutes nobody reads.
 *
 * The one session that cannot answer is the one the timer filed on its own,
 * because there was nobody there to ask. Those are left blank on purpose and
 * can be filled in afterwards.
 */

export const MAX_WORKED_ON_LENGTH = 200;

/** Null when it will do; otherwise something to show the person. */
export function describeWorkedOnProblem(name: string | null | undefined): string | null {
  const trimmed = (name ?? "").trim();
  if (trimmed === "") return "What did you work on?";
  if (trimmed.length > MAX_WORKED_ON_LENGTH) {
    return `Keep it under ${MAX_WORKED_ON_LENGTH} characters.`;
  }
  return null;
}

/* ── Attachments ───────────────────────────────────────────────────────────
 *
 * A photo of the page they got stuck on, an audio or phone video clip of the
 * bit that will not come out, a PDF of the music, or a link to the thing on YouTube they
 * were copying. All four are ways of showing a tutor something that a line of
 * text cannot, which is most of what goes wrong in practice.
 */

/**
 * 50 MB. A minute of phone video at 1080p is about this, which is the size
 * this is really for — anything longer belongs in a lesson, not an attachment.
 */
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

/** Short audio recordings use a smaller cap than video. */
export const MAX_AUDIO_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * What may be uploaded, by content type.
 *
 * A deliberate allowlist rather than a blocklist. These are served back to
 * browsers, and the set of things a browser will execute is not something to
 * discover by omission. HEIC is accepted because that is what an iPhone
 * produces, though most browsers will not preview it.
 */
export const ALLOWED_ATTACHMENT_TYPES: readonly string[] = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
  "application/pdf",
  "audio/aac",
  "audio/mp4",
  "audio/mpeg",
  "audio/ogg",
  "audio/wav",
  "audio/webm",
  "video/mp4",
  "video/webm",
  "video/quicktime",
];

/** Null when the file is acceptable; otherwise something to show the person. */
export function describeAttachmentProblem(fileType: string, fileSize: number): string | null {
  if (!ALLOWED_ATTACHMENT_TYPES.includes(fileType.toLowerCase())) {
    return "That kind of file cannot be attached — try a photo, audio, video or PDF.";
  }
  if (fileSize <= 0) return "That file is empty.";
  if (fileType.toLowerCase().startsWith("audio/") && fileSize > MAX_AUDIO_ATTACHMENT_BYTES) {
    return `That recording is larger than ${MAX_AUDIO_ATTACHMENT_BYTES / (1024 * 1024)} MB — try a shorter audio clip.`;
  }
  if (fileSize > MAX_ATTACHMENT_BYTES) {
    return `That file is larger than ${MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB — try a shorter clip.`;
  }
  return null;
}

/**
 * Audio and video uploads need to agree with their declared media type.
 * Browsers send a caller-controlled multipart MIME value, so an allowlist alone
 * is not enough. Other non-media legacy attachment types retain their checks.
 */
export function describeAttachmentContentProblem(fileType: string, bytes: Uint8Array): string | null {
  const type = fileType.toLowerCase();
  const audio = type.startsWith("audio/");
  const video = type.startsWith("video/");
  if (!audio && !video) return null;

  const asciiAt = (offset: number, value: string) =>
    offset + value.length <= bytes.length &&
    [...value].every((character, index) => bytes[offset + index] === character.charCodeAt(0));
  const startsWith = (value: string) => asciiAt(0, value);
  const boxSize = bytes.length >= 8
    ? bytes[0]! * 2 ** 24 + bytes[1]! * 2 ** 16 + bytes[2]! * 2 ** 8 + bytes[3]!
    : 0;
  const mp4Container =
    bytes.length >= 16 && asciiAt(4, "ftyp") && boxSize >= 16 && boxSize <= bytes.length;
  const ebmlContainer =
    bytes.length >= 4 && bytes[0] === 0x1a && bytes[1] === 0x45 &&
    bytes[2] === 0xdf && bytes[3] === 0xa3;

  const matches = type === "audio/aac"
    ? startsWith("ADIF") || (bytes.length >= 2 && bytes[0] === 0xff && (bytes[1]! & 0xf0) === 0xf0)
    : type === "audio/mp4"
      ? mp4Container
      : type === "audio/mpeg"
        ? startsWith("ID3") || (bytes.length >= 2 && bytes[0] === 0xff && (bytes[1]! & 0xe0) === 0xe0)
        : type === "audio/ogg"
          ? startsWith("OggS")
          : type === "audio/wav"
            ? (startsWith("RIFF") || startsWith("RF64") || startsWith("RIFX")) && asciiAt(8, "WAVE")
            : type === "audio/webm"
              ? ebmlContainer
              : type === "video/mp4"
                ? mp4Container
                : type === "video/webm"
                  ? ebmlContainer
                  : type === "video/quicktime"
                    ? mp4Container || (bytes.length >= 8 && ["moov", "mdat", "wide", "free"].some((atom) => asciiAt(4, atom)))
                    : false;

  return matches ? null : `That file does not match its declared ${audio ? "audio" : "video"} type.`;
}

/** How a browser should show a given attachment, in the absence of anything cleverer. */
export function attachmentKindOf(fileType: string): "image" | "audio" | "video" | "pdf" | "file" {
  const type = fileType.toLowerCase();
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("audio/")) return "audio";
  if (type.startsWith("video/")) return "video";
  if (type === "application/pdf") return "pdf";
  return "file";
}

/**
 * The video id inside a YouTube address, in any of the forms somebody will
 * actually paste: the share link, the address bar, a Short, an embed.
 *
 * Only YouTube. A link is embedded in a page a child is looking at, and
 * "anything you paste gets framed" is a different and much larger promise than
 * this feature is making — so anything else is refused with a reason rather
 * than stored and quietly not shown.
 */
export function youTubeVideoId(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  const host = url.hostname.toLowerCase().replace(/^www\./, "").replace(/^m\./, "");
  const path = url.pathname.split("/").filter(Boolean);
  const valid = (id: string | undefined) => (id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null);

  if (host === "youtu.be") return valid(path[0]);
  if (host === "youtube.com" || host === "youtube-nocookie.com") {
    if (path[0] === "watch") return valid(url.searchParams.get("v") ?? undefined);
    if (path[0] === "shorts" || path[0] === "embed" || path[0] === "live") return valid(path[1]);
  }
  return null;
}

/** The address to frame, from an id. Privacy-enhanced, which costs nothing. */
export function youTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}`;
}

/** Where YouTube itself serves the embed from, for a content security policy. */
export const YOUTUBE_EMBED_ORIGIN = "https://www.youtube-nocookie.com";

/* ── The timer ─────────────────────────────────────────────────────────────
 *
 * Pressing start and pressing stop is a truer record than remembering
 * afterwards, but it has one failure that typing does not: a phone goes in a
 * pocket, a battery dies, a tab is closed, and the clock keeps running. The
 * timer therefore asks after an hour whether anybody is still there.
 *
 * A locked/backgrounded phone cannot answer that question, so the question is
 * deliberately allowed to remain open for the rest of a maximum-length
 * session. If nobody ever comes back, expiry still credits only the time up to
 * the original prompt — never the hours of silence after it.
 *
 * All of it is decided from two timestamps held on the server, never from
 * anything the browser is counting. A page can be suspended while the screen
 * is off and catch up to the same elapsed time when it wakes again.
 */

/** How long it runs before asking whether anybody is still practising. */
export const TIMER_PROMPT_AFTER_MINUTES = 60;

/**
 * Keep the question available for the remainder of the longest valid session.
 * This is what lets a phone screen stay off without the five-minute UI grace
 * period killing a genuine practice timer in the background.
 */
export const TIMER_GRACE_MINUTES =
  MAX_SESSION_MINUTES - TIMER_PROMPT_AFTER_MINUTES;

/** Under a minute is a mis-tap on the start button, not a session. */
export const MIN_TIMER_SECONDS = 60;

export type TimerReading =
  /** Counting. Nothing is asked of anybody until `promptAt`. */
  | { state: "running"; elapsedSeconds: number; promptAt: Date; givesUpAt: Date }
  /** The question is on screen, and there is until `givesUpAt` to answer it. */
  | { state: "asking"; elapsedSeconds: number; promptAt: Date; givesUpAt: Date }
  /** Nobody answered. This is what should be filed, and it is final. */
  | { state: "expired"; elapsedSeconds: number; promptAt: Date; givesUpAt: Date };

/**
 * What a timer is doing, from when it started and when somebody last said they
 * were still at it.
 *
 * The credited length on expiry runs to the moment the question appeared, not
 * to the end of the long background-safe grace window. That preserves the old
 * anti-abandonment guarantee: `confirmedAt` starts equal to `startedAt`, so a
 * timer started and genuinely forgotten still credits one hour and no more.
 */
export function readTimer(
  startedAt: Date,
  confirmedAt: Date,
  now: Date = new Date(),
): TimerReading {
  const promptAt = new Date(confirmedAt.getTime() + TIMER_PROMPT_AFTER_MINUTES * 60_000);
  const backgroundSafeDeadline = promptAt.getTime() + TIMER_GRACE_MINUTES * 60_000;
  const hardSessionDeadline = startedAt.getTime() + MAX_SESSION_MINUTES * 60_000;
  const givesUpAt = new Date(Math.min(backgroundSafeDeadline, hardSessionDeadline));
  const since = (end: Date) => clampSessionSeconds((end.getTime() - startedAt.getTime()) / 1000);

  if (now.getTime() >= givesUpAt.getTime()) {
    return { state: "expired", elapsedSeconds: since(promptAt), promptAt, givesUpAt };
  }
  if (now.getTime() >= promptAt.getTime()) {
    return { state: "asking", elapsedSeconds: since(now), promptAt, givesUpAt };
  }
  return { state: "running", elapsedSeconds: since(now), promptAt, givesUpAt };
}
